/**
 * PATH: packages/email-hub/src/feature/components/email-allocation/DueBucketControl.tsx
 * FILE_ID: EMAILHUB-ALLOCATION-BUCKET-001-VIEW
 * PURPOSE: Renders due-bucket choices as pills or one compact overlay trigger.
 * WHY: Inline metadata has bounded width; four simultaneous pills created a
 *      nested horizontal scroller and pushed Watchers off-screen.
 */
import { useRef, useState } from 'react';
import { CalendarSearch, ChevronDown, Loader2 } from 'lucide-react';
import { AdaptiveOverlay } from '@quotech/ui';

import { cn } from '../../../_internal/lib/utils';
import { BUCKET_META, BUCKET_ORDER } from './helpers';
import type { AllocationBucket, DueBucketViewModel } from './types';

interface DueBucketControlProps {
  vm: DueBucketViewModel;
  /** Inline metadata uses one trigger; standalone cards keep all quick pills. */
  compact?: boolean;
  /** Change #269 P5 — "Pick date…" row in the compact overlay. Receives the
   *  operator's date-only ISO value (YYYY-MM-DD) exactly as typed/picked; no
   *  timezone conversion happens here (date-only strings never UTC-shift). */
  onPickDate?: (isoDate: string) => void;
  /** Controlled/uncontrolled hybrid for the compact overlay — context menus
   *  (due_pick_date) open the picker imperatively. Omit for internal state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function DueBucketPill({ bucket, vm }: { bucket: AllocationBucket; vm: DueBucketViewModel }) {
  const meta = BUCKET_META[bucket];
  const Icon = meta.icon;
  const active = vm.currentBucket === bucket;
  const busy = vm.busyKey === `bucket:${bucket}`;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={() => void vm.pickBucket(bucket)}
      disabled={!!vm.busyKey}
      className={cn(
        'inline-flex min-h-11 touch-manipulation items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border transition sm:min-h-0',
        active
          ? meta.tone
          : 'bg-background/40 border-border text-muted-foreground hover:bg-muted/60',
        busy && 'opacity-50'
      )}
      data-testid={`email-allocation-bucket-${bucket}`}
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" />}
      <span>{meta.label}</span>
    </button>
  );
}

function CompactDueBucketControl({
  vm,
  onPickDate,
  open: controlledOpen,
  onOpenChange,
}: Omit<DueBucketControlProps, 'compact'>) {
  const [internalOpen, setInternalOpen] = useState(false);
  // Show the inline date input only after the operator chose "Pick date…".
  const [dateEntry, setDateEntry] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const current = BUCKET_META[vm.currentBucket];
  const CurrentIcon = current.icon;
  const busy = vm.busyKey?.startsWith('bucket:') ?? false;

  const open = controlledOpen ?? internalOpen;
  const setOpen = (next: boolean) => {
    onOpenChange?.(next);
    if (controlledOpen === undefined) setInternalOpen(next);
    if (!next) setDateEntry(false);
  };

  const selectBucket = (bucket: AllocationBucket) => {
    setOpen(false);
    void vm.pickBucket(bucket);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(!open)}
        disabled={!!vm.busyKey}
        className={cn(
          'inline-flex min-h-11 max-w-full touch-manipulation items-center gap-1',
          'rounded-full border px-2 py-0.5 text-[11px] font-medium sm:min-h-0',
          current.tone,
          busy && 'opacity-50'
        )}
        data-testid="email-allocation-due-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CurrentIcon className="h-3 w-3" />}
        <span className="truncate">{current.label}</span>
        <ChevronDown className="h-3 w-3 shrink-0 opacity-60" />
      </button>
      <AdaptiveOverlay
        open={open}
        onOpenChange={setOpen}
        anchorRef={triggerRef}
        label="Choose when to action this email"
        mode="auto"
        popoverWidth={180}
        testId="email-allocation-bucket-popover"
      >
        <div className="p-1" role="listbox" aria-label="When to action this email">
          {BUCKET_ORDER.map((bucket) => {
            const meta = BUCKET_META[bucket];
            const Icon = meta.icon;
            return (
              <button
                key={bucket}
                type="button"
                role="option"
                aria-selected={bucket === vm.currentBucket}
                onClick={() => selectBucket(bucket)}
                className={cn(
                  'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[12px]',
                  'hover:bg-muted',
                  bucket === vm.currentBucket && 'bg-muted font-medium'
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                <span>{meta.label}</span>
              </button>
            );
          })}
          {onPickDate && !dateEntry && (
            <button
              type="button"
              onClick={() => setDateEntry(true)}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[12px] hover:bg-muted"
              data-testid="email-allocation-pick-date"
            >
              <CalendarSearch className="h-3.5 w-3.5" />
              <span>Pick date…</span>
            </button>
          )}
          {onPickDate && dateEntry && (
            <div className="px-2 py-1.5">
              <input
                type="date"
                autoFocus
                aria-label="Pick an exact date"
                data-testid="email-allocation-date-input"
                className="w-full rounded border border-border bg-background px-1.5 py-1 text-[12px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                onChange={(event) => {
                  if (!event.target.value) return;
                  setOpen(false);
                  onPickDate(event.target.value);
                }}
              />
            </div>
          )}
        </div>
      </AdaptiveOverlay>
    </>
  );
}

export function DueBucketControl({
  vm,
  compact = false,
  onPickDate,
  open,
  onOpenChange,
}: DueBucketControlProps) {
  if (compact) {
    return (
      <CompactDueBucketControl
        vm={vm}
        onPickDate={onPickDate}
        open={open}
        onOpenChange={onOpenChange}
      />
    );
  }

  return (
    <div
      className="flex items-center gap-1.5"
      data-testid="email-allocation-bucket-pills"
      role="radiogroup"
      aria-label="When to action this email"
    >
      {BUCKET_ORDER.map((bucket) => (
        <DueBucketPill key={bucket} bucket={bucket} vm={vm} />
      ))}
    </div>
  );
}
